import {
  CancelledScheduledMessage,
  Chat,
  ChatThread,
  ChatThreadKind,
  DispatchedScheduledMessage,
  JobStatus,
  MarkChatsAsRead,
  MessageChannel,
  MessageType,
  Route,
  ScheduledMessageGroup,
  SendEmailAsStoreStaff,
  SendSmsAsStoreStaff,
  SetMessageIsReadStatus,
  populateRoute,
} from "@repo/common"
import {
  QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"
import jobQueries from "./job-queries"
import storeCustomerQueries from "./store-customer-queries"

interface ChatFilters extends Partial<Chat> {
  storeId: string
}

async function fetchNumUnread(storeId: string): Promise<number> {
  return await request({
    url: populateRoute(
      Route.MESSAGE_CONTROLLER__FIND_NUM_UNREAD_MESSAGES,
      storeId,
    ),
    method: "GET",
  })
}

async function fetchChats(filters: ChatFilters): Promise<Chat[]> {
  const { storeId } = filters

  return await request<Chat[]>({
    url: populateRoute(
      Route.MESSAGE_CONTROLLER__FIND_MANY_CHATS_BY_STORE_ID,
      storeId,
    ),
    method: "GET",
  })
}

export async function createMessage(
  data: SendSmsAsStoreStaff | SendEmailAsStoreStaff,
): Promise<void> {
  const formData = new FormData()

  for (const [key, value] of Object.entries(data)) {
    if (key === "attachedFiles" && Array.isArray(value)) {
      for (const file of value) formData.append(key, file)
    } else if (value !== undefined) {
      formData.append(key, String(value))
    }
  }

  const route =
    data.type === MessageType.SMS_FROM_STORE_STAFF
      ? Route.MESSAGE_CONTROLLER__SEND_SMS_AS_STORE_STAFF
      : Route.MESSAGE_CONTROLLER__SEND_EMAIL_AS_STORE_STAFF

  return await request<void>({
    url: populateRoute(route, data.jobId),
    method: "POST",
    data: formData,
  })
}

export const useCreateMessage = (jobId: string, storeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SendSmsAsStoreStaff | SendEmailAsStoreStaff) =>
      createMessage(data),
    onSettled: async () =>
      await queryClient.invalidateQueries(jobQueries.detail(jobId)),
    onSuccess: () =>
      queryClient.invalidateQueries(chatQueries.list({ storeId })),
  })
}

interface ScheduleMessageArgs {
  content: string
  channels: MessageChannel[]
  files: File[]
  // Asks the API to put the message text in the SMS itself rather than a link
  // to the chat, accepting that a long message costs several segments. The API
  // sends the full text regardless when it fits in one segment.
  sendFullSms: boolean
  // Job chats only: a store customer's own chat reads the recipient from the
  // store customer and has no job status to change.
  toPhoneNumber?: string
  toEmail?: string
  // Applied by the API when the message is actually delivered, so undoing the
  // send leaves the job's status untouched.
  statusToSetOnSend?: JobStatus
}

/**
 * Persists the message without delivering it, opening the mechanic's undo
 * window. One request covers every selected channel, so the resulting rows
 * share a group id and can be cancelled together.
 */
export async function scheduleMessage(
  thread: ChatThread,
  {
    content,
    channels,
    toPhoneNumber,
    toEmail,
    files,
    statusToSetOnSend,
    sendFullSms,
  }: ScheduleMessageArgs,
): Promise<ScheduledMessageGroup> {
  const formData = new FormData()
  formData.append("content", content)
  // multipart does not carry repeated values reliably, so the API expects a
  // comma-separated list here.
  formData.append("channels", channels.join(","))
  formData.append("sendFullSms", String(sendFullSms))
  for (const file of files) formData.append("attachedFiles", file)

  if (thread.kind === ChatThreadKind.STORE_CUSTOMER) {
    return await request<ScheduledMessageGroup>({
      url: populateRoute(
        Route.MESSAGE_CONTROLLER__SCHEDULE_TO_STORE_CUSTOMER,
        thread.storeCustomerId,
      ),
      method: "POST",
      data: formData,
    })
  }

  formData.append("jobId", thread.jobId)
  if (toPhoneNumber) formData.append("toPhoneNumber", toPhoneNumber)
  if (toEmail) formData.append("toEmail", toEmail)
  if (statusToSetOnSend) {
    formData.append("statusToSetOnSend", statusToSetOnSend)
  }

  return await request<ScheduledMessageGroup>({
    url: populateRoute(
      Route.MESSAGE_CONTROLLER__SCHEDULE_AS_STORE_STAFF,
      thread.jobId,
    ),
    method: "POST",
    data: formData,
  })
}

/** Refetches the messages of a chat, wherever they are loaded from. */
export const invalidateChatThread = (
  queryClient: QueryClient,
  thread: ChatThread,
) =>
  thread.kind === ChatThreadKind.JOB
    ? queryClient.invalidateQueries(jobQueries.detail(thread.jobId))
    : Promise.all([
        queryClient.invalidateQueries(
          storeCustomerQueries.messages(thread.storeCustomerId),
        ),
        queryClient.invalidateQueries(
          storeCustomerQueries.detail(thread.storeCustomerId),
        ),
      ])

export const useScheduleMessage = (thread: ChatThread, storeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: ScheduleMessageArgs) => scheduleMessage(thread, args),
    onSettled: async () => {
      await Promise.all([
        invalidateChatThread(queryClient, thread),
        queryClient.invalidateQueries(chatQueries.list({ storeId })),
      ])
    },
  })
}

export async function cancelScheduledMessage(
  thread: ChatThread,
  groupId: string,
): Promise<CancelledScheduledMessage> {
  return await request<CancelledScheduledMessage>({
    url:
      thread.kind === ChatThreadKind.JOB
        ? populateRoute(
            Route.MESSAGE_CONTROLLER__CANCEL_SCHEDULED,
            thread.jobId,
            groupId,
          )
        : populateRoute(
            Route.MESSAGE_CONTROLLER__CANCEL_SCHEDULED_TO_STORE_CUSTOMER,
            thread.storeCustomerId,
            groupId,
          ),
    method: "DELETE",
  })
}

export const useCancelScheduledMessage = (
  thread: ChatThread,
  storeId: string,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (groupId: string) => cancelScheduledMessage(thread, groupId),
    onSettled: async () => {
      await Promise.all([
        invalidateChatThread(queryClient, thread),
        queryClient.invalidateQueries(chatQueries.list({ storeId })),
      ])
    },
  })
}

/**
 * Ends the undo window early. The API runs the same dispatch Cloud Tasks would
 * have run at 30 s, so a group that was already delivered or undone comes back
 * as { dispatched: false } rather than an error.
 */
export async function sendScheduledMessageNow(
  thread: ChatThread,
  groupId: string,
): Promise<DispatchedScheduledMessage> {
  return await request<DispatchedScheduledMessage>({
    url:
      thread.kind === ChatThreadKind.JOB
        ? populateRoute(
            Route.MESSAGE_CONTROLLER__SEND_SCHEDULED_NOW,
            thread.jobId,
            groupId,
          )
        : populateRoute(
            Route.MESSAGE_CONTROLLER__SEND_SCHEDULED_NOW_TO_STORE_CUSTOMER,
            thread.storeCustomerId,
            groupId,
          ),
    method: "POST",
  })
}

export const useSendScheduledMessageNow = (
  thread: ChatThread,
  storeId: string,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (groupId: string) => sendScheduledMessageNow(thread, groupId),
    onSettled: async () => {
      await Promise.all([
        invalidateChatThread(queryClient, thread),
        queryClient.invalidateQueries(chatQueries.list({ storeId })),
      ])
    },
  })
}

interface MessageReadStatus {
  id: string
  isRead: boolean
  isManuallyUnread: boolean
}

async function updateMessageReadStatus(
  thread: ChatThread,
  { id, isRead, isManuallyUnread }: MessageReadStatus,
): Promise<void> {
  if (thread.kind === ChatThreadKind.STORE_CUSTOMER) {
    return await request<void>({
      url: populateRoute(
        Route.MESSAGE_CONTROLLER__SET_STORE_CUSTOMER_MESSAGE_IS_READ_STATUS,
        thread.storeCustomerId,
        id,
      ),
      method: "PATCH",
      data: { isRead, isManuallyUnread },
    })
  }

  const data: SetMessageIsReadStatus = {
    id,
    isRead,
    isManuallyUnread,
    jobId: thread.jobId,
  }
  return await request<void>({
    url: populateRoute(Route.MESSAGE_CONTROLLER__SET_MESSAGE_IS_READ_STATUS),
    method: "PATCH",
    data,
  })
}

export const useUpdateMessageReadStatus = (
  thread: ChatThread,
  storeId: string,
) => {
  const queryClient = useQueryClient()

  const invalidate = () =>
    Promise.all([
      invalidateChatThread(queryClient, thread),
      queryClient.invalidateQueries(chatQueries.list({ storeId })),
      queryClient.invalidateQueries(chatQueries.numUnread(storeId)),
    ])

  return useMutation({
    mutationFn: (status: MessageReadStatus) =>
      updateMessageReadStatus(thread, status),
    onSettled: invalidate,
    onSuccess: invalidate,
  })
}

async function markChatsAsRead(data: MarkChatsAsRead): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.MESSAGE_CONTROLLER__MARK_CHATS_AS_READ),
    method: "PATCH",
    data,
  })
}

export const useMarkChatsAsRead = (storeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (threads: ChatThread[]) =>
      markChatsAsRead({
        storeId,
        jobIds: threads.flatMap((thread) =>
          thread.kind === ChatThreadKind.JOB ? [thread.jobId] : [],
        ),
        storeCustomerIds: threads.flatMap((thread) =>
          thread.kind === ChatThreadKind.STORE_CUSTOMER
            ? [thread.storeCustomerId]
            : [],
        ),
      }),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries(chatQueries.list({ storeId })),
        queryClient.invalidateQueries(chatQueries.numUnread(storeId)),
      ])
    },
    onSuccess: (_, threads) => {
      for (const thread of threads) {
        invalidateChatThread(queryClient, thread)
      }
    },
  })
}

const chatQueries = {
  all: () => ["chats"],
  lists: () => [...chatQueries.all(), "list"],
  list: (filters: ChatFilters) =>
    queryOptions({
      queryKey: [...chatQueries.lists(), filters],
      queryFn: () => fetchChats(filters),
    }),
  numUnread: (storeId: string) =>
    queryOptions({
      queryKey: [...chatQueries.all(), "num-unread"],
      queryFn: () => fetchNumUnread(storeId),
      staleTime: 5000,
    }),
}

export default chatQueries
